import { getConfigProps } from '@/utils/get-config-props';
import { AnalyticsStepDefinition } from '@/types/AnalyticsStepDefinition';
import { ReplacingMarkup } from '@/types/AuditEvent';

/* eslint-disable @typescript-eslint/type-annotation-spacing */
/* eslint-disable @typescript-eslint/member-delimiter-style */
/* eslint-disable no-multi-spaces */

export type ConfigUpdater = Partial<Config> & {
    fixedTopFn?: Function | string;
    fixedBottomFn?: Function | string;
    eventHook?: Function | string;
    loadHook?: Function | string;
    beforeInitialRequest?: Function | string;
    mainMenuHook?: Function | string;
    chatLoadedHook?: Function | string;
    chatClosedHook?: Function | string;
}

export class Config {
    id        : Readonly<number | string>;
    index     : Readonly<number>;
    container?: string;            // css selector for widget container
    // eslint-disable-next-line @typescript-eslint/type-annotation-spacing
    clinicId                     = null as string | number | null;
    apiUrl                       = getConfigProps().apiUrl;
    staticUrl                    = getConfigProps().staticUrl;
    runnerApiBaseUrl?: string;                                                               // runner-bff base URL for the runner UI mode (override for non-prod)
    mode                         = 'normal' as 'normal' | 'chat' | 'search' | 'inline';     // mode of widget
    rtl                          = false;                                                   // Right-To-Left
    firstWeekDay                 = 0;                                                       // Calendar's grid offset
    geoLocation                  = null as { longitude: number, latitude: number } | null;  // predefined geo location
    useBrowserHistory            = false;                                                   // control pages from browsers back/forward
    stepsByQuestions             = true;                                                    // back/forward travels through the questions
    hasLeaveConfirmation         = false;                                                   // enable confirmation for leaveing the page
    showRequiredMark             = true;                                                    // show required mark on blocks
    forceAdaptive                = true;                                                    // force blocks to be adaptive
    swipeCardInputDelay          = 1500;                                                    // ms before swipecard block submits the step
    language                     = 'english';                                               // language of the widget
    chatMode                     = false;                                                   // DEPRECATED
    simpleMode                   = false;                                                   // DEPRECATED
    storeSessionAutosubmitRegExp = '(\\?|&)odr-autoSubmit=true($|#|&)';                     // Regexp to match on stored session to autosubmit last step
    storeSessionTTL              = 1000 * 60 * 10;                                          // TTL of storedSession
    xmlParser                    = false;                                                   // use XML parser for markup from responses
    reportErrors                 = true;                                                    // send widget errors to api
    dynamsoftLicenceKey          = 'f0068WQAAAI2AiXNSnc7ub4lUu4XM1WTvQAqlsJbJedGJC7nFM+9qmXBI1aHOIyrGun7yIf/WZvBkiKePh8PDN7+0ZdWlfeg='
    addParams?: Record<string, string>;                                                     // additional params to be sent on initial request, would be sent only once
    initParams = { _embedingUrl: window.location.href } as Record<string, string>           // params to be sent on initial request

    // Date and Time
    ampm                         = true;                                                    // Time format output
    defaultStartTime             = '6:00';                                                  // Default Start Time for TimePicker
    defaultEndTime               = '21:00';                                                 // Default End Time for TimePicker
    minuteStep                   = '15';                                                    // Default minute step for TimePicker

    // Network
    addPreload            = true;                   // add link[rel="preload"] for chat/search modes
    mapLoadingRetries     = 3;                      // Google map loading retries
    xhrTimeout            = 10000;                  // Timeout for ajax requests
    xhrSlotsDebounce      = 250;                    // ms to debouce xhr request for slots
    xhrSuggestDebounce    = 250;                    // ms to debouce xhr request for suggest
    slotsRetries          = 5;                      // number of slots fetching retries when something goes wrong
    suggestLimit          = 100;                    // number of entries to fetch on more
    refreshSessionTimeout = null as null | number;  // ms to force redirection to main menu after session timed out
    refreshPageTimeout    = null as null | number;  // ms to page reload timeout
    mainMenuReloadTimeout = 1000 * 60 * 60 * 4 as null | number;  // ms to force page reload when returning to main menu (default: 4 hours)

    // Google Map
    googleMapsKey = 'AIzaSyCg07HsQHnN2rq2_dNGkWIEY_yxu7U8ix4';
    googleMapsStyles?: Record<string, string>;  // style object for google maps styling
    mapMinZoom = 14;  // Minimum map zoom level (more value -> more magnification)

    // Appearance
    zIndex                            = 900;                                                   // z-index for positioned elements
    specificViewWidth                 = 1600;                                                  // width of the widget for specific view
    regularMaxWidth                   = 800;                                                   // max width of the widget
    sidenotesWidgetMaxWidth           = 1000;                                                  // max width of the widget
    numOfFieldsBreakPoints            = [5, 8];                                                // num of fields break points for columns
    minColumnWidth                    = 320;                                                   // min width of column (min field width (50) + side paddings (30) * 4)

    languageSwitcher                  = false;                                                 // show language switcher
    stickyHeader                      = false;                                                 // sticky header
    footer                            = true;                                                  // show footer with odoro logo
    stepsCenteredVertical             = false;                                                 // center steps vertical on normal mode
    stepsCenteredHorizontal           = false;                                                 // center steps horizontal on normal mode
    stepsCenteredWidth                = 1000;                                                  // width of centered steps on normal mode
    hideHeader                        = false;                                                 // hide top header
    customizing                       = {} as Record<string, string>;                          // widget styling
    showIndexesOnProgress             = 500;                                                   // width to show indexes instead of titles on progress bar
    transitionTime                    = 200;                                                   // collapsing/expanding steps speed
    unfoldLastOnPage                  = true;                                                  // Unfold last step on back/forward
    fixedTop                          = 0;                                                     // Fixed element's top offset
    fixedBottom                       = 0;                                                     // Fixed element's bottom offset
    fixedTopFn                        = function (this: Config) { return this.fixedTop; };     // Fixed element's top offset
    fixedBottomFn                     = function (this: Config) { return this.fixedBottom; };  // Fixed element's bottom offset
    headerMarkupSection               = null as null | string;
    providerListFilterSuggestMoreThan = 9;                                                     // Show suggest in filters if count of options is more than
    scrollIntoStepOffset              = 150;                                                   // If the widget's position is higher than this offset then scrolling on step would lead to start of the page.
    dropdownOnTopOffset               = 40;                                                    // Add space above when dropdown on top mode
    isSelfScheduling                  = true;                                                  // Indicate the self-scheduling UI mode
    comboboxPlaceholderWidthThreshold = 135;                                                   // If combobox's width is smaller than this threshold (in px) then the default placeholder switch from $trl('aw.combobox_placeholder') to $trl('aw.combobox_placeholder_short') for fully visible

    // Chat mode
    chatTitle          = 'Configurable title';                                // title in chat's header
    chatModeFullScreen = false;
    chatButtonImage    = null as null | string;                               // image on opener
    chatButtonHeader   = 'Need an appointment? Easy schedule with Chat-bot';  // title on closed embed panel
    chatWidth          = 450;
    chatMarginSide     = 10;                                                  // Side margin of chat window
    chatMarginBottom   = 10;                                                  // Bottom margin of chat window
    chatHeight         = '70%' as string | number;                            // Chat width, numbers for pixels, and string like '50%' for percentage
    chatPositionLeft   = false;                                               // position on the left side of the screen
    chatModeDelay      = 300;                                                 // delay between messages appears
    chatAvatar         = null as null | string;                               // chat bot's avatar
    chatAvatarColor    = 'var(--odr-color-main)' as null | string;            // chat bot's avatar color
    chatHideOpenButton = false;                                               // hides the floating chat opener button
    chatHasCloseButton = true;
    chatOpenedByDefault = false;

    // Search mode
    searchPlaceholder  = 'Enter a condition, speciality, practice, or provider name';
    searchTitle        = 'Schedule Your<br>Appointment Online';
    searchNoBackground = false;
    searchShowTerms    = true;
    searchMaxWidth     = 600;
    searchBlockId      = 'searchbox';

    // Analytics
    eventHook               = function () {} as (event: {type: string; value: AnalyticsStepDefinition}) => void;  // events for tracking user progress, event.value - dataQa value, event.type - type of event
    googleAnalyticsId       = 'UA-144356082-1' as null | string | string[];                    // GA_MEASURMENT_ID for internal google analyicts tracking
    clientsGtagId           = null as null | string;                                           // client's GA_MEASURMENT_ID for Gtag usages
    sendInternalEvents      = false;                                                            // send analytics events to odoro
    sendClientsEvents       = false;                                                           // send analytics events to client's page
    useClientsGa            = true;                                                            // use existing google analytics (analytics.js)
    useClientsGtag          = true;                                                            // use existing google analytics (Gtag)
    analyticsPagePrefix     = 'odr';                                                           // virtual widget page prefix for analytics
    customDataDimension     = 'dimension15'                                                    // dimension for custom events data

    // Audit
    trackFlowAudit = false;

    // Hooks
    loadHook             = function (callback: Function) { callback(); } as (callback: Function) => void;                   // call callback to start widget
    beforeInitialRequest = function (callback: Function) { callback(); } as (callback: Function, config: Config) => void;  // before initial request hook
    mainMenuHook         = function (callback: Function) { callback(); } as (callback: Function, config: Config) => void;  // hook on clicking mainMenu button
    chatLoadedHook         = function () {}
    chatClosedHook         = function () {}

    // Debug
    kangaroo               = false;
    stub                   = undefined as undefined | Array<string> | string;  // stub to load on flow requests
    stubSuggest            = null as null | Function;                          // stub to load on suggest requests
    testRequestStepTimeout = 3000 ;

    // List of tags name that come from application configuration need to be escaped
    escapeMarkupTags: string[] = [];

    // List of tags name that will be escaped by default
    escapeMarkupTagsByDefault: string[] = [
        'highlight-location',
        'icon',
        'history-title',
        'history-value',
        'location',
        'avatar',
        'main-button',
        'submit-button',
        'clear-button',
        'print-button',
        'back-button',
        'hint',
        'stars',
        'toggle-trigger',
        'history-step',
    ];

    // Odoro Markup tags to be replacing by default
    defaultReplacingMarkups: ReplacingMarkup[] = [
        { tag: 'br', replaceStartTagWith: '\n', replaceEndTagWith: '\n' },
        { tag: 'table-cell', replaceStartTagWith: ' ', replaceEndTagWith: ' ' },
        { tag: 'table-row', replaceStartTagWith: '\n', replaceEndTagWith: '' },
        { tag: 'row', replaceStartTagWith: '\n', replaceEndTagWith: '' },
        { tag: 'info', replaceStartTagWith: '\n', replaceEndTagWith: '' },
        { tag: 'column', replaceStartTagWith: ' ', replaceEndTagWith: ' ' },
    ];

    // Odoro Markup tags to be replacing
    replacingMarkupTags: ReplacingMarkup[] = [];

    update (props: ConfigUpdater) {
        let preparedProps = { ...props };

        [
            'fixedTopFn',
            'fixedBottomFn',
            'eventHook',
            'loadHook',
            'beforeInitialRequest',
            'mainMenuHook',
            'chatLoadedHook',
            'chatClosedHook',
        ].forEach((key) => {
            delete preparedProps[key];

            if (typeof (props[key]) === 'string') {
                try {
                    // eslint-disable-next-line no-eval
                    preparedProps[key] = eval(`(${props[key]})`); ;
                } catch (error) {
                    const eError = {
                        configError: `Failed to parse config.${key} = "${props[key]}"`,
                        info: error.toString(),
                    };
                    console.error(eError);
                }
            } else if (typeof (props[key]) === 'function') {
                preparedProps[key] = props[key];
            }
        });

        Object.assign(this, {
            ...preparedProps,
            customizing: {
                ...(this.customizing),
                ...(props.customizing || {}),
            },
            initParams: {
                ...(this.initParams),
                ...(props.initParams || {}),
            },
        });
    }

    constructor (props: ConfigUpdater & Pick<Config, 'index'>) {
        this.update(props);
        this.index = props.index;
        this.id = props.id ?? props.index;
        let kangarooMode = JSON.parse(String(window.localStorage.getItem('__odr_kangarooMode:' + props.index)));
        if (kangarooMode) {
            this.mode = kangarooMode;
        }
    }
}
